import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import moment from 'moment'
import computeNiceTicks from '@/common/utils/computeNiceTicks'
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts'
import Money from '@/common/models/money'
import { Rocket } from 'lucide-react'
import PrecisionAwarePriceLabel from '@/common/models/precisionAwarePriceLabel'
import { cn } from '../utils/utils'
import { Button } from './button'
import { useNavigate } from 'react-router-dom'
import { APP_ROUTES } from '../constants'
import { analytics } from '../analytics/analytics'

/**
 * Compute a set of 'nice' tick values between rawMin and rawMax using 1-2-5 steps.
 * Returns { min, max, ticks } where min/max are the rounded domain boundaries and
 * ticks is an array of numeric tick positions (length <= tickCount+1).
 */
// computeNiceTicks is implemented in src/common/utils/computeNiceTicks.ts

type TooltipPayload = Array<{
    payload?: { x: number; y: number; percent?: number | null }
}>

const CustomTooltip = React.memo(({ payload }: { payload?: TooltipPayload }) => {
    const point = (payload && payload[0] && payload[0].payload) || null
    if (!point) return null
    const amount = Number.isFinite(point.y) ? point.y : null
    const date = moment(point.x).format('D MMM YY').toString()

    // percent should be precomputed and attached to the payload as `percent`
    const pct = point.percent
    const isPctNumber = typeof pct === 'number'
    const isZero = isPctNumber ? pct === 0 : false
    const isPositive = isPctNumber ? pct > 0 : false
    const formattedPct = isPctNumber ? `${isPositive ? '+' : isZero ? '' : '-'}${Math.abs(pct).toFixed(2)}%` : ''

    return (
        <div className="p-2 bg-white rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-300 inline-flex justify-start items-start">
            <div className="bg-white inline-flex flex-col justify-center items-start">
                <div className="text-center justify-start text-content-secondary text-xs font-normal font-['Inter'] leading-5">
                    {date}
                </div>
                <div className="inline-flex justify-start items-center gap-2">
                    <div className="text-center justify-start text-black text-base font-semibold font-['Inter'] leading-6">
                        {amount != null ? Money.of(amount).toString() : ''}
                    </div>
                    {isPctNumber && (
                        <div
                            className={cn(
                                "text-center justify-start text-sm font-medium font-['Inter'] leading-5",
                                isPositive ? 'text-success' : !isZero ? 'text-destructive' : 'text-neutral-500',
                            )}
                        >
                            {formattedPct} day
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
})

export function PortfolioValueGraph({
    data,
    tickCount = 5,
    isFake = false,
    isFetchingMore = false,
    onZoom,
}: {
    data: { x: number; y: number; percent?: number | null }[]
    tickCount?: number
    isFake?: boolean
    isFetchingMore?: boolean
    onZoom?: (domain: [number, number]) => void
}) {
    const navigate = useNavigate()
    const ys = useMemo(() => (data || []).map((d) => d.y).filter((n) => Number.isFinite(n)), [data])
    const hasY = ys.length > 0
    const rawMin = hasY ? Math.min(...ys) : 0
    const rawMax = hasY ? Math.max(...ys) : 0
    const {
        min: domainMin,
        max: domainMax,
        ticks: yTicks,
    } = useMemo(() => computeNiceTicks(rawMin, rawMax, tickCount), [rawMin, rawMax, tickCount])

    // data is expected to already contain numeric timestamps for `x`
    const numericData = data

    // Compute X domain (ms timestamps) from numeric data and keep it in state
    const computedXDomain = useMemo(() => {
        const xs = (data || []).map((d) => d.x).filter((n) => Number.isFinite(n))
        if (xs.length === 0) return undefined
        const min = Math.min(...xs)
        const max = Math.max(...xs)
        // add a small padding on the right only so the line isn't compressed
        // against the right edge, but keep the left bound exactly at the
        // earliest data point so the first value sits flush to the Y axis.
        const range = max - min
        let pad = range * 0.02
        // if range is zero (single point), provide a one-day right padding
        if (pad === 0) pad = 24 * 60 * 60 * 1000
        return [min, max + pad] as [number, number]
    }, [data])

    // The raw data extent (no padding) — use this for tick placement so ticks
    // line up with actual data points. We keep `computedXDomain` (padded)
    // for the visual chart domain to provide breathing room at edges.
    const computedXExtent = useMemo(() => {
        const xs = (data || []).map((d) => d.x).filter((n) => Number.isFinite(n))
        if (xs.length === 0) return undefined
        const min = Math.min(...xs)
        const max = Math.max(...xs)
        return [min, max] as [number, number]
    }, [data])

    const [xDomain, setXDomain] = useState<[number, number] | undefined>(computedXDomain)
    const xDomainRef = useRef<typeof xDomain>(xDomain)

    useEffect(() => {
        xDomainRef.current = xDomain
    }, [xDomain])

    // Smoothly animate xDomain changes (programmatic zoom on data change)
    useEffect(() => {
        if (!computedXDomain) return

        if (!xDomainRef.current) {
            setXDomain(computedXDomain)
            xDomainRef.current = computedXDomain
            if (onZoom) onZoom(computedXDomain)
            return
        }

        const from = xDomainRef.current
        const to = computedXDomain
        if (from[0] === to[0] && from[1] === to[1]) return

        let rafId = 0
        const duration = 420
        const start = performance.now()

        const step = (now: number) => {
            const t = Math.min(1, (now - start) / duration)
            const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t // quadratic-ish ease
            const a0 = from[0] + (to[0] - from[0]) * ease
            const a1 = from[1] + (to[1] - from[1]) * ease
            setXDomain([a0, a1])
            if (t < 1) {
                rafId = requestAnimationFrame(step)
            } else {
                // finalize and notify parent
                setXDomain(to)
                if (onZoom) onZoom(to)
            }
        }

        rafId = requestAnimationFrame(step)
        return () => cancelAnimationFrame(rafId)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [computedXDomain])

    // X tick formatter (for numeric timestamps)
    const xTickFormatter = useCallback((ts: number) => {
        if (!Number.isFinite(ts)) return ''
        return moment(ts).format('MMM DD').toString()
    }, [])

    // Responsive X tick count based on viewport width
    const [xTickCount, setXTickCount] = useState<number>(() => {
        const w = window.innerWidth
        if (w < 480) return 2
        if (w < 768) return 3
        if (w < 1024) return 4
        if (w < 1280) return 5
        return 6
    })

    useEffect(() => {
        const onResize = () => {
            const w = window.innerWidth
            if (w < 480) setXTickCount(2)
            else if (w < 768) setXTickCount(3)
            else if (w < 1024) setXTickCount(4)
            else if (w < 1280) setXTickCount(5)
            else setXTickCount(6)
        }
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [])

    const xTicks = useMemo((): number[] | undefined => {
        // Prefer the unpadded data extent for ticks so labels correspond to
        // real data points; fall back to the padded domain if extent missing.
        const extent = computedXExtent ?? computedXDomain
        if (!extent) return undefined
        const [minX, maxX] = extent
        if (!Number.isFinite(minX) || !Number.isFinite(maxX)) return undefined
        const count = Math.max(2, xTickCount || 2)
        const ticks: number[] = []
        const span = maxX - minX
        for (let i = 0; i < count; i++) {
            const t = Math.round(minX + span * (i / (count - 1)))
            ticks.push(t)
        }
        return ticks
    }, [computedXExtent, computedXDomain, xTickCount])

    const renderXTick = useCallback(
        ({ x, y, payload }: any) => {
            const value = (payload && (payload.value ?? payload)) as number
            const minX = computedXExtent ? computedXExtent[0] : undefined
            const isFirst = minX !== undefined && Math.abs(value - minX) < 1
            const label = xTickFormatter(value)
            return (
                <text
                    x={x}
                    y={y + 12}
                    textAnchor={isFirst ? 'start' : 'middle'}
                    className="text-sm fill-current text-content-secondary font-['Inter']"
                >
                    {label}
                </text>
            )
        },
        [computedXExtent, xTickFormatter],
    )

    const leftMargin = useMemo(() => {
        const longest = yTicks
            .map((tick) => PrecisionAwarePriceLabel.of(tick, true, isFake).toString().length)
            .reduce((max, len) => Math.max(max, len), 0)
        // left margin: 8px for every character over 5 (no fixed base), capped to avoid layout break
        return Math.min(140, Math.max(0, longest - 5) * 8)
    }, [yTicks, isFake])

    const tooltipContent = useCallback(({ payload }: any) => <CustomTooltip payload={payload} />, [])

    return (
        <div className="relative w-full">
            {/* non-blocking fetch indicator when more data is loading */}
            {isFetchingMore && (
                <div className="absolute right-3 top-3 z-20 bg-white/80 px-2 py-1 rounded shadow-sm text-xs text-stone-600">
                    Loading more…
                </div>
            )}
            <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={numericData || []} margin={{ top: 10, left: leftMargin, bottom: 0 }}>
                    <defs>
                        <linearGradient id="colorClose" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#E4FBC6" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#E4FBC6" stopOpacity={0} />
                        </linearGradient>
                    </defs>

                    <XAxis
                        dataKey="x"
                        type="number"
                        domain={xDomain}
                        scale="time"
                        tickFormatter={xTickFormatter}
                        ticks={xTicks}
                        tick={renderXTick}
                        allowDataOverflow={true}
                        tickSize={0}
                        tickMargin={6}
                        strokeOpacity={0.4}
                    />

                    <YAxis
                        className="text-sm"
                        strokeWidth={1}
                        strokeOpacity={0.2}
                        tickFormatter={(value) => PrecisionAwarePriceLabel.of(value, true, isFake).toString()}
                        ticks={yTicks}
                        padding={{ bottom: 10 }}
                        dataKey="y"
                        type="number"
                        domain={hasY ? [domainMin, domainMax] : undefined}
                    />

                    {!isFake && <Tooltip content={tooltipContent} />}

                    {(!data || data.length === 0) && (
                        <text x="50%" y="50%" style={{ fill: '#32871D' }} textAnchor="middle">
                            Loading
                        </text>
                    )}

                    {data && data.length > 0 && (
                        <Area
                            strokeWidth={2}
                            type="linear"
                            dataKey="y"
                            stroke={isFake ? '#737671' : '#32871D'}
                            fillOpacity={isFake ? 0 : 1}
                            fill="url(#colorClose)"
                            isAnimationActive={true}
                            animationDuration={420}
                        />
                    )}
                </AreaChart>
            </ResponsiveContainer>

            {isFake && (
                <div className="ml-[50px] absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-0">
                    <Button
                        className="inline-flex items-center rounded-2xl px-4 py-2 shadow"
                        variant="outline"
                        onClick={() => {
                            analytics.trackWithDefaults('your_investing_journey_starts_here_selected', {
                                source_page: 'dashboard',
                            })
                            navigate(APP_ROUTES.INVEST)
                        }}
                    >
                        <div className="flex-1 flex justify-start items-start gap-4">
                            <Rocket className="text-stone-600" />
                            <div className="self-stretch justify-center text-content-primary text-sm font-medium font-['Inter'] leading-5">
                                Your investing journey starts here
                            </div>
                        </div>
                    </Button>
                </div>
            )}
        </div>
    )
}
